import React, { Fragment, HTMLAttributes, memo } from 'react';
import { useSelector } from 'react-redux';
import TextEllipsis from '../../../Components/TextEllipsis';
import { TimeUtil } from 'core/utils/date';
import {
  IProduct,
  IProductAvailableTime,
} from 'core/redux/features/products/types';
import { useSelectProductAvailableTimes, useTrans } from 'core/hooks';
import classNames from 'classnames';
import { ActiveFiltersType } from '../../../core/redux/features/filters/types';
import { AppState } from '../../../core/redux/reducers';

function isMatchTime(
  filters: ActiveFiltersType,
  availableTime: IProductAvailableTime,
) {
  const {
    times: [activeTimeStart, activeTimeStop],
    person,
  } = filters;
  const parseActiveTimeStart = TimeUtil.parse(activeTimeStart);
  const parseActiveTimeStop = TimeUtil.parse(activeTimeStop);
  let parseCompareTime = TimeUtil.parse(availableTime.time);
  if (availableTime.time_end) {
    parseCompareTime = TimeUtil.parse(availableTime.time_end);
  }

  const isTimeAvailable =
    parseCompareTime.gte(parseActiveTimeStart) &&
    parseCompareTime.lte(parseActiveTimeStop);

  const isTimeHasAvailableSeat =
    !person || person === 0 || availableTime.seats.indexOf(person) > -1;

  return isTimeAvailable && isTimeHasAvailableSeat;
}

export const TimeList = function ({
  product,
  separator,
}: {
  product: IProduct;
  separator: React.ReactNode;
}) {
  const trans = useTrans();
  const timeList = useSelectProductAvailableTimes(product);
  const activeFilter = useSelector(
    (state: AppState) => state.filters.activeFilters,
  );

  if (timeList.length === 0) {
    return <span className="is-uppercase">{trans('no_times')}</span>;
  }

  let timeLength = 0;
  if (timeList) {
    timeLength = timeList.length - 1;
  }

  return (
    <>
      {timeList.map((rawTime, timeIndex) => {
        let timeStart = TimeUtil.parse(rawTime.time).format('HH.MM');
        if (rawTime.time_end) {
          timeStart += ' - ' + TimeUtil.parse(rawTime.time_end).format('HH.MM');
        }

        return (
          <Fragment key={timeIndex}>
            <span
              className={
                !isMatchTime(activeFilter, rawTime) ? 'time-separator' : ''
              }
            >
              {timeStart}
            </span>
            {timeLength > timeIndex ? separator : null}
          </Fragment>
        );
      })}
    </>
  );
};

type TProps = {
  product: IProduct;
  label?: string | null | boolean;
  disabledEllipsis?: boolean;
  format?: string;
  separator?: React.ReactNode;
} & HTMLAttributes<HTMLDivElement>;

function TimeListForProduct({
  separator,
  format,
  product,
  disabledEllipsis,
  label,
  className,
  ...htmlAttr
}: TProps) {
  return (
    <>
      <TextEllipsis
        className={classNames(className, 'product__times')}
        {...htmlAttr}
        pretend={disabledEllipsis}
      >
        {product ? (
          <TimeList
            product={product}
            separator={separator || <span className="time-separator">, </span>}
          />
        ) : null}
      </TextEllipsis>
    </>
  );
}

TimeListForProduct.defaultProps = {
  disabledEllipsis: false,
  format: 'HH.MM',
  label: null,
};

export const ProductTimeList = memo(
  TimeListForProduct,
  function ({ product: product1 }, { product: product2 }) {
    return product1.id === product2.id;
  },
);
